// Legacy IngestPackage migration — applied BEFORE Zod parse.
//
// Why this module exists (Session 19AP · 陛下):
//   The CaseSchema in 19AE collapsed three alternate naming fields
//   (`title_en`, `title_zh`, `name`) into a single `title`, and renamed
//   SubSection.`label` → `title`. The collapse comment in case.ts (line 11)
//   asserted "Brand course migrated by frontend `migrateIngestPackage`",
//   but no such function ever existed in the codebase — paste-json-input.tsx
//   was running IngestPackageSchema.safeParse() directly, so any JSON paste
//   carrying legacy field names rejected with `cases.N.title: Required`.
//
// Design:
//   Each migration is a small declarative entry (id + introducedIn +
//   rationale + scope + apply) so future schema bumps add ONE entry rather
//   than touch a monolithic function. The id is the field rename arrow
//   ("case.title_en→title") for telemetry/UI banners ("已自动迁移 N 处遗留
//   字段"). `applied[]` returned from the entry point lets the caller log,
//   surface, or count what fired.
//
//   Mirrors the spirit of `migrateDraft` in packages/web/src/storage/idb.ts
//   (forward-fill defaults onto persisted shapes), but registry-keyed so
//   individual rules are independently auditable.
//
// Type narrowing (19BO+ Line 4.4.3):
//   Legacy data is by definition not type-conformant — the whole point of
//   this module is to reach into untyped shapes. We narrow at the boundary
//   (`migrateLegacyIngestPackage` guards raw → `Record<string, unknown>`)
//   and each `apply()` does ad-hoc runtime checks (`Array.isArray`,
//   `typeof === 'string'`) before mutating. No `any` annotations remain —
//   the `@typescript-eslint/no-explicit-any` rule sees every type position
//   in this file un-disabled. (One line-level `no-console` disable exists
//   on the dev-only diagnostic at the rule-error catch; that rule guards
//   a different concern — logging hygiene, not type safety.)
//
// Out of scope (intentionally):
//   - safeParse: the caller (paste-json-input, idb result-load) decides.
//   - Backwards migration (new→old): exam-prep is forward-only; we never
//     downgrade.
//
// To extend (future schema bumps):
//   1. Append a new entry to MIGRATIONS below with `introducedIn` = session id.
//   2. Keep `apply()` idempotent — re-running on already-migrated data must
//      be a noop (no flag set, no field clobber). Migration order is
//      registration order; apply() should not depend on later migrations.
//   3. If a rename is total (old field deleted from schema), DO delete it
//      from the cloned package so safeParse sees a clean shape.
/**
 * Item-collection array fields on IngestPackage that hold tagged items.
 * Used by the v3→v4 wrap migration below. Adding a new item kind = one entry.
 *
 * Excludes `mockExamItems` (single object with nested paper-level subarrays,
 * not a flat array of items — different shape, different migration when v4
 * mock paper LLM wireup lands · DEBT-19BH-MOCK-LLM-WIREUP).
 */
const V3_V4_ITEM_ARRAY_FIELDS = [
    'flashcards',
    'mcqs',
    'trueFalse',
    'essays',
    'glossary',
    'mindmaps',
    'proofDrills',
    'lectureNotes',
    'freeResponses',
    'readingPassages',
];
/**
 * Wrap a single v3-shape item's singular tag fields into v4 array form.
 * Idempotent — re-running on a v4-shaped item is a noop (preserves the array
 * fields verbatim). Singular alias fields are preserved so 19BH+1 frontend code
 * not yet migrated to plural reads still resolve (Step 5.2 writes the same alias).
 *
 * Returns true iff the item was mutated.
 */
function wrapItemTagsV3ToV4InPlace(item) {
    if (!item || typeof item !== 'object')
        return false;
    const it = item;
    let changed = false;
    if (!Array.isArray(it['caseIds'])) {
        const caseId = it['caseId'];
        it['caseIds'] = typeof caseId === 'string' ? [caseId] : [];
        changed = true;
    }
    if (!Array.isArray(it['topicIds'])) {
        const topicId = it['topicId'];
        it['topicIds'] = typeof topicId === 'string' ? [topicId] : [];
        changed = true;
    }
    if (!Array.isArray(it['conceptIds'])) {
        const conceptTag = it['conceptTag'];
        it['conceptIds'] = typeof conceptTag === 'string' ? [conceptTag] : [];
        changed = true;
    }
    return changed;
}
/**
 * Pad concept array to schema floor (3). Used by deriveOutlineFromCasesV3
 * since CurriculumOutlineSchema requires `concepts.min(3)` per chapter — old
 * v3 packages with sparse topicHierarchy can fall below this floor. Padded
 * concepts carry an `__inferred: true` passthrough flag the v4 audit framework
 * picks up to surface as `outline_concept_inferred` warn-level GapEntry. User
 * regenerates the outline via the v4 lock-outline endpoint to clear them.
 */
function padConceptsToFloor(concepts, caseId) {
    const padded = [...concepts];
    let i = padded.length;
    while (padded.length < 3) {
        padded.push({
            id: `${caseId}_concept_${i + 1}`,
            label: `Auto-derived placeholder ${i + 1} · regenerate outline to refine`,
            __inferred: true,
        });
        i++;
    }
    return padded.slice(0, 12); // also clamp to schema ceiling
}
/**
 * Derive a minimal CurriculumOutline from v3 cases + optional topicHierarchy.
 * Used by the v3→v4 outline-derive migration entry below. Pure function;
 * exposed for unit testing and for the IDB onupgradeneeded path (idb.ts).
 *
 * Mapping rules:
 *   - One ChapterAnchor per Case (id, order, title from Case)
 *   - Topics: filter topicHierarchy by parentId === caseId + level === 'topic'.
 *     If none, single placeholder topic so schema `topics.min(1)` passes.
 *   - Concepts: filter topicHierarchy by level === 'subtopic' AND parent topic
 *     belongs to this chapter. Padded to floor (3) and clamped to ceiling (12).
 *     Synthetic concept ids carry `__inferred: true` (passthrough) for audit.
 *   - source: 'llm_generated_frozen' (closest match; outline didn't exist in v3
 *     so origin attribution is best-effort).
 */
export function deriveOutlineFromCasesV3(cases, topicHierarchy) {
    const hierarchy = Array.isArray(topicHierarchy) ? topicHierarchy : [];
    const topicNodesByParent = new Map();
    const subtopicNodesByParent = new Map();
    for (const rawNode of hierarchy) {
        if (!rawNode || typeof rawNode !== 'object')
            continue;
        const node = rawNode;
        const parentId = typeof node['parentId'] === 'string' ? node['parentId'] : null;
        if (!parentId)
            continue;
        const nodeId = node['id'];
        const entry = { id: String(nodeId), name: String(node['name'] ?? nodeId) };
        const level = node['level'];
        if (level === 'topic') {
            const list = topicNodesByParent.get(parentId) ?? [];
            list.push(entry);
            topicNodesByParent.set(parentId, list);
        }
        else if (level === 'subtopic') {
            const list = subtopicNodesByParent.get(parentId) ?? [];
            list.push(entry);
            subtopicNodesByParent.set(parentId, list);
        }
    }
    const chapters = cases.map((rawCase, idx) => {
        const c = (rawCase && typeof rawCase === 'object' ? rawCase : {});
        const caseId = String(c['id']);
        const rawTopics = topicNodesByParent.get(caseId) ?? [];
        const caseTitle = c['title'];
        const topics = rawTopics.length > 0
            ? rawTopics.slice(0, 5).map((t) => ({ id: t.id, title: t.name }))
            : [{ id: `${caseId}_topic_1`, title: typeof caseTitle === 'string' ? caseTitle : caseId }];
        // Concepts = all subtopics belonging to any topic under this chapter,
        // plus any direct-to-case subtopics if hierarchy is mal-shaped.
        const concepts = [];
        for (const t of rawTopics) {
            const subs = subtopicNodesByParent.get(t.id) ?? [];
            for (const s of subs) {
                concepts.push({ id: s.id, label: s.name });
            }
        }
        const directSubs = subtopicNodesByParent.get(caseId) ?? [];
        for (const s of directSubs) {
            concepts.push({ id: s.id, label: s.name });
        }
        const order = c['order'];
        return {
            id: caseId,
            order: typeof order === 'number' ? order : idx + 1,
            title: typeof caseTitle === 'string' && caseTitle.length > 0 ? caseTitle : caseId,
            importance: 2,
            topics,
            concepts: padConceptsToFloor(concepts, caseId),
        };
    });
    return {
        outlineSchemaVersion: 'v1',
        source: 'llm_generated_frozen',
        // Stable epoch timestamp — onupgradeneeded calls must be deterministic so
        // test snapshots stay reproducible. Callers that want "real now" pass new
        // Date().toISOString() into a fresh wrapper.
        generatedAt: '1970-01-01T00:00:00.000Z',
        chapters,
    };
}
/** Migration registry. Order = registration order. Append-only. */
const MIGRATIONS = [
    {
        id: 'case.title_en|title_zh|name→title',
        introducedIn: '19AE',
        scope: 'cases',
        rationale: 'CaseSchema 19AE collapsed legacy alternate-naming fields (title_en, title_zh, name) into a single `title`. v1 IB JSON files and any pre-19AE paste use the old names.',
        apply: (pkg) => {
            const cases = pkg['cases'];
            if (!Array.isArray(cases))
                return false;
            let changed = false;
            for (const rawCase of cases) {
                if (!rawCase || typeof rawCase !== 'object')
                    continue;
                const c = rawCase;
                // Already migrated? Skip — keeps idempotency.
                if (typeof c['title'] === 'string' && c['title'].length > 0)
                    continue;
                const titleEn = c['title_en'];
                const titleZh = c['title_zh'];
                const name = c['name'];
                const fallback = (typeof titleEn === 'string' && titleEn.length > 0 && titleEn) ||
                    (typeof titleZh === 'string' && titleZh.length > 0 && titleZh) ||
                    (typeof name === 'string' && name.length > 0 && name) ||
                    null;
                if (fallback) {
                    c['title'] = fallback;
                    changed = true;
                }
            }
            return changed;
        },
    },
    {
        id: 'case.subSection.label→title',
        introducedIn: '19AE',
        scope: 'cases.subSections',
        rationale: 'SubSection.label renamed to title in 19AE for naming consistency with the parent Case.title.',
        apply: (pkg) => {
            const cases = pkg['cases'];
            if (!Array.isArray(cases))
                return false;
            let changed = false;
            for (const rawCase of cases) {
                if (!rawCase || typeof rawCase !== 'object')
                    continue;
                const c = rawCase;
                const subSections = c['subSections'];
                if (!Array.isArray(subSections))
                    continue;
                for (const rawSs of subSections) {
                    if (!rawSs || typeof rawSs !== 'object')
                        continue;
                    const ss = rawSs;
                    const title = ss['title'];
                    if (typeof title === 'string' && title.length > 0)
                        continue;
                    const label = ss['label'];
                    if (typeof label === 'string' && label.length > 0) {
                        ss['title'] = label;
                        changed = true;
                    }
                }
            }
            return changed;
        },
    },
    {
        id: 'item.{caseId,topicId,conceptTag}→{caseIds,topicIds,conceptIds}[]',
        introducedIn: '19BH',
        scope: 'items.tags',
        rationale: 'v4 (Sprint 19BH) makes ItemTags universal: every LLM-generated item across 10 collections has caseIds/topicIds/conceptIds arrays. v3 used singular caseId/topicId/conceptTag. This wraps the singular form into single-element arrays so the v4 IngestPackageSchema parses old data without rejection. Singular fields stay as alias for engine code not yet migrated (removed in 19BI).',
        apply: (pkg) => {
            let changed = false;
            for (const field of V3_V4_ITEM_ARRAY_FIELDS) {
                const arr = pkg[field];
                if (!Array.isArray(arr))
                    continue;
                for (const item of arr) {
                    if (wrapItemTagsV3ToV4InPlace(item))
                        changed = true;
                }
            }
            return changed;
        },
    },
    {
        id: 'pkg.curriculumOutline.derive-from-cases',
        introducedIn: '19BH',
        scope: 'pkg.curriculumOutline',
        rationale: 'v4 ingest pillar B (CurriculumOutline) is the frozen anchor for chapter→topic→concept ids. v3 packages have no outline field; this synthesizes a minimal outline from cases (+ optional topicHierarchy). Synthetic concepts carry __inferred: true so the v4 audit framework surfaces them as gaps the user can resolve via the lock-outline endpoint.',
        apply: (pkg) => {
            const existingOutline = pkg['curriculumOutline'];
            if (existingOutline && typeof existingOutline === 'object')
                return false;
            const cases = pkg['cases'];
            if (!Array.isArray(cases) || cases.length === 0)
                return false;
            // cases is `unknown[]` after Array.isArray narrowing; cast matches the
            // ReadonlyArray<unknown> param contract (runtime shape verified above).
            pkg['curriculumOutline'] = deriveOutlineFromCasesV3(cases, pkg['topicHierarchy']);
            return true;
        },
    },
    {
        id: 'pkg.gapReport.default-empty',
        introducedIn: '19BH',
        scope: 'pkg.gapReport',
        rationale: 'v4 audit Phase 4 emits a GapReport. v3 packages have no gapReport field. Default empty matches finalize.ts behavior (defaultEmptyGapReport) so UI / regen-context resolver always see uniform shape. Frontend can re-run /ingest/audit on demand to compute a real report.',
        apply: (pkg) => {
            const existing = pkg['gapReport'];
            if (existing && typeof existing === 'object')
                return false;
            pkg['gapReport'] = {
                // Deterministic epoch — see deriveOutlineFromCasesV3 note above.
                generatedAt: '1970-01-01T00:00:00.000Z',
                invariantsRun: [],
                entries: [],
                summary: { errorCount: 0, warnCount: 0, infoCount: 0 },
            };
            return true;
        },
    },
    {
        id: 'pkg.schemaVersion→0.3.0',
        introducedIn: '19BH',
        scope: 'pkg.schemaVersion',
        rationale: 'After v3→v4 migration runs, the package shape matches IngestPackage v0.3.0 (item-tags arrays + curriculumOutline + gapReport). Bump the self-tag so meetsMinSchemaVersion("0.3.0") returns true for migrated artefacts.',
        apply: (pkg) => {
            // Only stamp 0.3.0 when at least one earlier 19BH rule fired — detect
            // via gapReport presence (added by the pkg.gapReport rule above OR by
            // a fresh v4 ingest). Idempotent: re-running on 0.3.0 data is a noop.
            const current = pkg['schemaVersion'];
            if (typeof current === 'string' && current === '0.3.0')
                return false;
            if (!pkg['gapReport'])
                return false; // earlier rules didn't fire → not a v4-shaped pkg
            pkg['schemaVersion'] = '0.3.0';
            return true;
        },
    },
];
/**
 * Migrate a *raw* IngestPackage-shaped value to the current schema shape,
 * forward-only. Idempotent: re-running on an already-migrated package
 * returns `applied: []` and the same shape.
 *
 * Intended use:
 * ```ts
 * const { migrated, applied } = migrateLegacyIngestPackage(raw);
 * const parsed = IngestPackageSchema.safeParse(migrated);
 * if (applied.length > 0) toast(`Migrated ${applied.length} legacy fields`);
 * ```
 */
export function migrateLegacyIngestPackage(raw) {
    // Reject non-object inputs early; let the Zod parse downstream produce the
    // canonical error message rather than us inventing one here.
    if (typeof raw !== 'object' || raw === null) {
        return { migrated: raw, applied: [] };
    }
    // JSON deep-clone — IngestPackages are pure data (no functions, no Date,
    // no Map) by schema construction, so JSON round-trip is faithful and a
    // few orders of magnitude faster than structuredClone for typical sizes.
    // Annotated `unknown` to isolate the `any` that `JSON.parse` returns; the
    // Array guard below narrows to the object-shape every entry expects.
    const cloned = JSON.parse(JSON.stringify(raw));
    // Arrays aren't a valid IngestPackage shape — treat like a primitive input
    // and hand the raw value back for Zod to produce the canonical error.
    if (Array.isArray(cloned) || cloned === null || typeof cloned !== 'object') {
        return { migrated: cloned, applied: [] };
    }
    const pkg = cloned;
    const applied = [];
    for (const m of MIGRATIONS) {
        try {
            if (m.apply(pkg))
                applied.push(m.id);
        }
        catch (err) {
            // A migration throwing means a rule is itself buggy; surface in dev,
            // never block the parse — let Zod produce the user-facing error.
            // `no-console` is disabled because a buggy migration rule is exactly
            // the kind of dev-time signal we want surfaced (and we deliberately
            // don't `throw` — that would block the parse the surrounding contract
            // promises to preserve).
            // eslint-disable-next-line no-console
            console.error(`[migrate-legacy] rule "${m.id}" threw:`, err);
        }
    }
    return { migrated: pkg, applied };
}
/** Read-only registry listing — for documentation / debug / tests. */
export function listLegacyMigrations() {
    return MIGRATIONS.map(({ id, introducedIn, scope, rationale }) => ({ id, introducedIn, scope, rationale }));
}
//# sourceMappingURL=migrate-legacy.js.map